import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Gavel, ExternalLink, Search, Bookmark, BookmarkCheck, RefreshCw, Info,
  ChevronLeft, ChevronRight, Star, Trash2, Plus, SquareCheck, Check,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import StyledSelect from "../components/ui/StyledSelect";
import TenderModal from "../components/TenderModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../lib/auth";

const PAGE_SIZE = 10;

function parseClosingDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d) ? null : d;
}

function daysUntil(dateStr) {
  const d = parseClosingDate(dateStr);
  if (!d) return null;
  const diff = d.getTime() - Date.now();
  return diff <= 0 ? 0 : Math.ceil(diff / 86400000);
}

function isClosed(dateStr) {
  const d = parseClosingDate(dateStr);
  return !!d && d.getTime() <= Date.now();
}

function closesToday(dateStr) {
  const d = parseClosingDate(dateStr);
  if (!d || isClosed(dateStr)) return false;

  const istDay = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format;

  return istDay(d) === istDay(new Date());
}

function fmtDeadline(dateStr) {
  const d = parseClosingDate(dateStr);
  if (!d) return dateStr || "—";

  return (
    new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(d) + " IST"
  );
}

function relevanceClass(score) {
  if (typeof score !== "number") return "bg-[#F1F5F9] text-[#64748B]";
  if (score <= 45) return "bg-[#FEE2E2] text-[#DC2626]"; // <45 red (45 inclusive)
  if (score <= 70) return "bg-[#FEF3C7] text-[#92400E]"; // 46-70 yellow/amber
  return "bg-[#DCFCE7] text-[#15803D]"; // >70 green
}

// Best-effort numeric parse of a free-text value
function parseValue(text) {
  if (!text) return 0;

  const raw = String(text).trim().toLowerCase();

  if (
    raw === "na" ||
    raw === "n/a" ||
    raw === "-" ||
    raw === "—" ||
    raw === "not available"
  ) {
    return 0;
  }

  const s = raw.replace(/,/g, "");
  const m = s.match(/([\d.]+)/);

  if (!m) return 0;

  let n = parseFloat(m[1]);
  if (isNaN(n)) return 0;

  if (/crore|\bcr\b/.test(s)) n *= 1e7;
  else if (/lakh|\blac\b/.test(s)) n *= 1e5;

  return n;
}

const STATUS_OPTIONS = [
  { v: "all", label: "All Statuses" },
  { v: "open", label: "Open" },
  { v: "closing_soon", label: "Closing soon (≤14d)" },
  { v: "closed", label: "Closed" },
];

const VALUE_OPTIONS = [
  { v: "all", label: "All Values" },
  { v: "lt1", label: "Under ₹1 Cr" },
  { v: "1to10", label: "₹1 Cr – ₹10 Cr" },
  { v: "gt10", label: "Over ₹10 Cr" },
];

const DEADLINE_OPTIONS = [
  { v: "all", label: "Any Time" },
  { v: "7", label: "Within 7 days" },
  { v: "30", label: "Within 30 days" },
  { v: "90", label: "Within 90 days" },
];

// Strategic-recommendation workflow filter. Separate from Status above,
// which is the tender's deadline lifecycle.
const REC_OPTIONS = [
  { v: "all", label: "All" },
  { v: "open", label: "Open" },
  { v: "closed", label: "Closed" },
];

const SORT_OPTIONS = [
  { v: "deadline_asc", label: "Deadline (Soonest)" },
  { v: "deadline_desc", label: "Deadline (Latest)" },
  { v: "value_desc", label: "Value (High to Low)" },
  { v: "value_asc", label: "Value (Low to High)" },
  { v: "relevance", label: "Relevance" },
];

function DeadlineCell({ t }) {
  const dleft = daysUntil(t.closing_date);
  const closed = isClosed(t.closing_date);

  let color = "text-[#059669]";
  let label;

  if (closed) {
    color = "text-[#94A3B8]";
    label = "Closed";
  } else if (dleft === null) {
    color = "text-[#64748B]";
    label = "No deadline";
  } else if (closesToday(t.closing_date)) {
    color = "text-[#DC2626]";
    label = "Closes today";
  } else if (dleft <= 7) {
    color = "text-[#DC2626]";
    label = `${dleft} days left`;
  } else if (dleft <= 21) {
    color = "text-[#B45309]";
    label = `${dleft} days left`;
  } else {
    label = `${dleft} days left`;
  }

  return (
    <div>
      <div className={`text-sm font-semibold ${color}`}>
        {label}
      </div>
      <div className="text-xs text-[#94A3B8] mt-0.5">
        {fmtDeadline(t.closing_date)}
      </div>
    </div>
  );
}


// const openTenderExternal = (t) => {
//   if (!t.url) return;

//   // Some portals (NIC/eProcure family) gate deep links behind a session
//   // cookie that only gets set by first visiting the portal's own entry
//   // page. Warm up that session in the new tab, then navigate it to the
//   // actual tender deep link.
//   const win = window.open(t.source_url || t.url, "_blank");

//   if (win && t.source_url && t.source_url !== t.url) {
//     setTimeout(() => {
//       try {
//         win.location.href = t.url;
//       } catch {
//         // If cross-origin restrictions somehow block this, the user is
//         // still left on the warmed-up portal page as a fallback.
//       }
//     }, 1500);
//   }
// };

// const openTenderExternal = (t) => {
//   if (!t.source_url && !t.url) return;

//   // t.url is a deep "$DirectLink"-style URL that NIC/eProcure-family
//   // portals (mahatenders, uktenders, etc.) generate — it's encrypted
//   // against the specific server-side session that existed when our
//   // crawler visited. That session is typically discarded by the server
//   // within minutes, so reopening this exact link later always fails with
//   // "session out / stale session", even from a freshly-cookied browser.
//   // There's no reliable client-side fix for that, so instead: send the
//   // user to the portal's own search page, with the Tender ID copied to
//   // their clipboard so they can paste it straight in.
//   if (t.tender_id) {
//     navigator.clipboard?.writeText(t.tender_id).then(
//       () => toast.success(`Tender ID "${t.tender_id}" copied — paste it into the portal's search box.`),
//       () => toast.info(`Search for Tender ID "${t.tender_id}" on the portal that opens.`)
//     );
//   } else {
//     toast.info("Opening the portal — search using the tender title shown here.");
//   }

//   window.open(t.source_url || t.url, "_blank");
// };

// const openTenderExternal = (t) => {
//   const target = t.source_url || t.url;
//   if (!target) return;

//   // Open first, synchronously — never let anything else risk blocking this.
//   window.open(target, "_blank");

//   if (t.tender_id) {
//     try {
//       navigator.clipboard
//         ?.writeText(t.tender_id)
//         .then(() => {
//           toast.success(`Tender ID "${t.tender_id}" copied — paste it into the portal's search box.`);
//         })
//         .catch(() => {
//           toast.info(`Search for Tender ID "${t.tender_id}" on the portal that just opened.`);
//         });
//     } catch {
//       toast.info(`Search for Tender ID "${t.tender_id}" on the portal that just opened.`);
//     }
//   } else {
//     toast.info("Opening the portal — search using the tender title shown here.");
//   }
// };


// const openTenderExternal = (t) => {
//   const deepLink = t.url;
//   const baseUrl = t.source_url;

//   if (!deepLink) return;

//   if (!baseUrl || baseUrl === deepLink) {
//     window.open(deepLink, "_blank");
//     return;
//   }

//   // Must open the tab SYNCHRONOUSLY inside the click handler, or the
//   // browser's popup blocker will kill it. Show a lightweight placeholder
//   // while the session gets warmed up behind the scenes.
//   const win = window.open("", "_blank");
//   if (win) {
//     win.document.write(
//       '<p style="font-family:sans-serif;padding:2rem;color:#475569">Connecting to portal…</p>'
//     );
//   }

//   // Hidden iframe makes a first-party request to the portal's own entry
//   // page, which is meant to set its session cookie in the browser — the
//   // same effect as visiting it yourself first.
//   const iframe = document.createElement("iframe");
//   iframe.style.display = "none";
//   iframe.src = baseUrl;

//   let done = false;
//   const finish = () => {
//     if (done) return;
//     done = true;
//     if (win && !win.closed) win.location.href = deepLink;
//     iframe.remove();
//   };

//   iframe.onload = finish;
//   setTimeout(finish, 4000); // fallback if onload never fires

//   document.body.appendChild(iframe);
// };





function OpeningDateCell({ t }) {
  const d = parseClosingDate(t.opening_date);

  if (!d) {
    return (
      <div>
        <div className="text-sm font-semibold text-[#64748B]">
          No opening date
        </div>
        <div className="text-xs text-[#94A3B8] mt-0.5">
          —
        </div>
      </div>
    );
  }

  const diff = d.getTime() - Date.now();
  const days = Math.ceil(diff / 86400000);

  let color = "text-[#059669]";
  let label;

  if (days < 0) {
    color = "text-[#94A3B8]";
    label = "Opened";
  } else if (days === 0) {
    color = "text-[#DC2626]";
    label = "Opens today";
  } else if (days === 1) {
    color = "text-[#B45309]";
    label = "Opens in 1 day";
  } else {
    label = `Opens in ${days} days`;
  }

  return (
    <div>
      <div className={`text-sm font-semibold ${color}`}>
        {label}
      </div>

      <div className="text-xs text-[#94A3B8] mt-0.5">
        {fmtDeadline(t.opening_date)}
      </div>
    </div>
  );
}


function EPublishDateCell({ t }) {
  const d = parseClosingDate(t.e_publish_date);

  if (!d) {
    return (
      <div>
        <div className="text-sm font-semibold text-[#64748B]">
          No publish date
        </div>
        <div className="text-xs text-[#94A3B8] mt-0.5">
          —
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-sm font-semibold text-[#475569]">
        Published
      </div>

      <div className="text-xs text-[#94A3B8] mt-0.5">
        {fmtDeadline(t.e_publish_date)}
      </div>
    </div>
  );
}

export default function Tenders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenders, setTenders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [bookmarkIds, setBookmarkIds] = useState(new Set());

  const [projectId, setProjectId] = useState(() => searchParams.get("project_id") || "all");
  const [status, setStatus] = useState("all");
  const [recStatus, setRecStatus] = useState("all");
  const [valueBand, setValueBand] = useState("all");
  const [deadline, setDeadline] = useState(() => searchParams.get("deadline") || "all");
  const [sort, setSort] = useState("deadline_asc");
  const [search, setSearch] = useState("");
  const [savedOnly, setSavedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const viewsKey = `lr_tender_views_${user?.id || "anon"}`;

  const [activeTender, setActiveTender] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [pendingDeleteView, setPendingDeleteView] = useState(null);
  // Single + bulk delete confirmation. { kind: 'one'|'bulk', item, items }
  // (mirrors Feed.jsx pendingDelete pattern)
  const [pendingDelete, setPendingDelete] = useState(null);

  // Multi-select (mirrors Feed.jsx selectMode/selected pattern)
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const [tenderLinkModal, setTenderLinkModal] = useState(null); // holds the tender object, or null
  const [copied, setCopied] = useState(false);


  const openTenderExternal = (t) => {
    setCopied(false);
    setTenderLinkModal(t);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy — please select and copy manually.");
    }
  };

  const visitPortal = (t) => {
    window.open(t.source_url || t.url, "_blank");
  };

  useEffect(() => {
    try {
      setSavedViews(
        JSON.parse(localStorage.getItem(viewsKey) || "[]")
      );
    } catch {
      setSavedViews([]);
    }
  }, [viewsKey]);

  const persistViews = (v) => {
    setSavedViews(v);
    localStorage.setItem(viewsKey, JSON.stringify(v));
  };

  const saveCurrentView = () => {
    const name = window.prompt(
      "Name this view (e.g. 'Solar · closing soon')"
    );

    if (!name || !name.trim()) return;

    const view = {
      id: Date.now().toString(),
      name: name.trim(),
      projectId,
      status,
      recStatus,
      valueBand,
      deadline,
      sort,
      savedOnly,
      search,
    };

    persistViews([...savedViews, view]);
    toast.success("View saved");
  };

  const applyView = (v) => {
    setProjectId(v.projectId ?? "all");
    setStatus(v.status ?? "all");
    setRecStatus(v.recStatus ?? "all");
    setValueBand(v.valueBand ?? "all");
    setDeadline(v.deadline ?? "all");
    setSort(v.sort ?? "deadline_asc");
    setSavedOnly(!!v.savedOnly);
    setSearch(v.search ?? "");
  };

  const deleteView = (id) => {
    const view = savedViews.find((v) => v.id === id) || null;
    setPendingDeleteView(view);
  };

  const confirmDeleteView = () => {
    if (!pendingDeleteView) return;
    persistViews(savedViews.filter((v) => v.id !== pendingDeleteView.id));
    setPendingDeleteView(null);
    toast.success("Saved view deleted");
  };

  const openTender = (t) => {
    setActiveTender(t);
    setModalOpen(true);
  };

  // Recommendation workflow: sync open/closed + note into the list and
  // the open modal. Toast is shown by the modal.
  const onTenderActionStatusChange = (t, data) => {
    const patch = {
      action_status: data.action_status,
      action_closed_note: data.action_closed_note ?? null,
      action_closed_at: data.action_closed_at ?? null,
    };
    setTenders((prev) => prev.map((x) => (x.id === t.id ? { ...x, ...patch } : x)));
    setActiveTender((prev) => (prev && prev.id === t.id ? { ...prev, ...patch } : prev));
  };

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => {
        const list = r.data?.projects || [];
        setProjects(list);
        // Honor ?project_id= deep-links (e.g. feed banner); ignore unknown ids.
        const pid = searchParams.get("project_id");
        if (pid && list.some((p) => p.id === pid)) setProjectId(pid);
      })
      .catch(() => {})
      .finally(() => setProjectsLoaded(true));

    api
      .get("/tenders/bookmark-ids")
      .then((r) => setBookmarkIds(new Set(r.data?.ids || [])))
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);

    api
      .get("/tenders", {
        params: { include_closed: true },
      })
      .then((r) => setTenders(r.data?.tenders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const projectMap = useMemo(() => {
    const m = {};

    projects.forEach((p) => {
      m[p.id] = p.name;
    });

    return m;
  }, [projects]);

  const projectOptions = useMemo(
    () => [
      { v: "all", label: "All Projects" },
      ...projects.map((p) => ({
        v: p.id,
        label: p.name,
      })),
    ],
    [projects]
  );

  const selectedProject = projects.find((p) => p.id === projectId);
  const projectRunning = !!selectedProject?.is_running;

  const filtered = useMemo(() => {
    let out = tenders.slice();

    if (projectId !== "all") {
      out = out.filter((t) => t.project_id === projectId);
    }

    if (savedOnly) {
      out = out.filter((t) => bookmarkIds.has(t.id));
    }

    if (status !== "all") {
      out = out.filter((t) => {
        const dleft = daysUntil(t.closing_date);
        const closed = isClosed(t.closing_date);

        if (status === "closed") return closed;
        if (status === "open") return !closed;

        if (status === "closing_soon") {
          return !closed && dleft !== null && dleft <= 14;
        }

        return true;
      });
    }

    // Recommendation workflow filter. Missing action_status predates the
    // workflow and counts as open.
    if (recStatus !== "all") {
      out = out.filter((t) => {
        if (!t.action_required) return false;
        if (recStatus === "closed") return t.action_status === "closed";
        return (t.action_status || "open") === "open";
      });
    }

    if (valueBand !== "all") {
      out = out.filter((t) => {
        const v = parseValue(t.value);

        if (v === null) return false;
        if (valueBand === "lt1") return v < 1e7;
        if (valueBand === "1to10") return v >= 1e7 && v <= 1e8;
        if (valueBand === "gt10") return v > 1e8;

        return true;
      });
    }

    if (deadline !== "all") {
      const lim = parseInt(deadline);

      out = out.filter((t) => {
        const dleft = daysUntil(t.closing_date);

        return (
          !isClosed(t.closing_date) &&
          dleft !== null &&
          dleft <= lim
        );
      });
    }

    if (search.trim()) {
      const s = search.trim().toLowerCase();

      out = out.filter(
        (t) =>
          (t.tender_id || "").toLowerCase().includes(s) ||
          (t.title || "").toLowerCase().includes(s)
      );
    }

    const val = (t) => {
      const v = parseValue(t.value);
      return v === null ? -1 : v;
    };

    const dl = (t) =>
      parseClosingDate(t.closing_date)?.getTime() ??
      Number.MAX_SAFE_INTEGER;

    out.sort((a, b) => {
      switch (sort) {
        case "deadline_desc":
          return dl(b) - dl(a);

        case "value_desc":
          return val(b) - val(a);

        case "value_asc":
          return val(a) - val(b);

        case "relevance":
          return (
            (b.relevance_score || 0) - (a.relevance_score || 0) ||
            dl(a) - dl(b)
          );

        default:
          return dl(a) - dl(b);
      }
    });

    return out;
  }, [
    tenders,
    projectId,
    savedOnly,
    bookmarkIds,
    status,
    recStatus,
    valueBand,
    deadline,
    search,
    sort,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    projectId,
    recStatus,
    savedOnly,
    status,
    valueBand,
    deadline,
    search,
    sort,
  ]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (deadline && deadline !== "all") next.set("deadline", deadline);
    else next.delete("deadline");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PAGE_SIZE)
  );

  const pageItems = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const activeFilters =
    (projectId !== "all" ? 1 : 0) +
    (status !== "all" ? 1 : 0) +
    (recStatus !== "all" ? 1 : 0) +
    (valueBand !== "all" ? 1 : 0) +
    (deadline !== "all" ? 1 : 0) +
    (search ? 1 : 0) +
    (savedOnly ? 1 : 0);

  const clearFilters = () => {
    setProjectId("all");
    setStatus("all");
    setRecStatus("all");
    setValueBand("all");
    setDeadline("all");
    setSearch("");
    setSavedOnly(false);
  };

  const toggleBookmark = async (t) => {
    const isOn = bookmarkIds.has(t.id);

    setBookmarkIds((prev) => {
      const next = new Set(prev);

      if (isOn) next.delete(t.id);
      else next.add(t.id);

      return next;
    });

    try {
      if (isOn) {
        await api.delete(`/tenders/${t.id}/bookmark`);
      } else {
        await api.post(`/tenders/${t.id}/bookmark`);
      }
    } catch {
      setBookmarkIds((prev) => {
        const next = new Set(prev);

        if (isOn) next.add(t.id);
        else next.delete(t.id);

        return next;
      });

      toast.error("Could not update bookmark");
    }
  };

  const requestDeleteTender = (t) => {
    setPendingDelete({ kind: "one", item: t });
  };

  // Multi-select helpers (mirrors Feed.jsx)
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const toggleSelect = useCallback((t) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t.id)) next.delete(t.id);
      else next.add(t.id);
      return next;
    });
  }, []);

  const isAllSelected =
    filtered.length > 0 && selected.size >= filtered.length;

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((t) => t.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, isAllSelected]);

  const askBulkHide = () => {
    if (selected.size === 0) return;
    const toDelete = tenders.filter((t) => selected.has(t.id));
    setPendingDelete({ kind: "bulk", items: toDelete });
  };

  const performHideOne = async (t) => {
    const tenderId = t.id;

    // Optimistic: remove from local state immediately.
    setTenders((prev) => prev.filter((x) => x.id !== tenderId));
    setSelected((prev) => {
      if (!prev.has(tenderId)) return prev;
      const next = new Set(prev);
      next.delete(tenderId);
      return next;
    });

    try {
      await api.delete(`/tenders/${tenderId}`);
      toast.success("Removed from your feed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/tenders/${tenderId}/hide`);
              setTenders((prev) => [t, ...prev]);
              toast.success("Restored");
            } catch {
              toast.error("Could not restore");
            }
          },
        },
      });
    } catch {
      // Roll back on failure.
      setTenders((prev) => [t, ...prev]);
      toast.error("Could not hide tender");
    }
  };

  const performBulkHide = async (toDelete) => {
    const ids = toDelete.map((x) => x.id);
    const snapshot = new Map(toDelete.map((x) => [x.id, x]));

    setTenders((prev) => prev.filter((x) => !ids.includes(x.id)));
    setSelected(new Set());
    setSelectMode(false);

    const chunk = (arr, size) => {
      const out = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    };

    try {
      let n = 0;
      try {
        // Chunk to stay under server max-length limits and keep payloads small.
        const batches = chunk(ids, 400);
        for (const batch of batches) {
          const r = await api.post("/tenders/bulk-hide", { tender_ids: batch });
          n += r.data?.hidden ?? batch.length;
        }
      } catch (bulkErr) {
        // Fallback for older backends without bulk-hide: hide one by one in chunks.
        if (bulkErr?.response?.status === 404 || bulkErr?.response?.status === 405) {
          const batches = chunk(ids, 50);
          for (const batch of batches) {
            await Promise.all(batch.map((id) => api.delete(`/tenders/${id}`)));
          }
          n = ids.length;
        } else {
          throw bulkErr;
        }
      }
      toast.success(`${n} ${n === 1 ? "tender" : "tenders"} removed from your feed`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const undoBatches = chunk(ids, 50);
              for (const batch of undoBatches) {
                await Promise.all(batch.map((id) => api.delete(`/tenders/${id}/hide`)));
              }
              setTenders((prev) => {
                const existing = new Set(prev.map((x) => x.id));
                const restored = toDelete.filter((x) => !existing.has(x.id));
                return [...restored, ...prev];
              });
              toast.success("Restored");
            } catch {
              // Best-effort local restore even if server undo partially fails.
              setTenders((prev) => {
                const existing = new Set(prev.map((x) => x.id));
                const restored = ids
                  .map((id) => snapshot.get(id))
                  .filter((x) => x && !existing.has(x.id));
                return [...restored, ...prev];
              });
              toast.error("Could not restore all");
            }
          },
        },
      });
    } catch (e) {
      // Roll back on failure and surface the real reason.
      setTenders((prev) => {
        const existing = new Set(prev.map((x) => x.id));
        const restored = toDelete.filter((x) => !existing.has(x.id));
        return [...restored, ...prev];
      });
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === "one") performHideOne(pendingDelete.item);
    else performBulkHide(pendingDelete.items);
    setPendingDelete(null);
  };

  // Keep for backwards-compat with any external references; delegates to unified flow.
  const confirmDeleteTender = async () => {
    confirmDelete();
  };

  // Keep page in range after deletes shrink the list.
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (page > pages) setPage(pages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  const crawlNow = async () => {
    if (projectId === "all") {
      toast.error(
        "Select a specific project to crawl its tender portals."
      );
      return;
    }

    if (projectRunning) return;

    setCrawling(true);
    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? { ...project, is_running: true }
          : project
      )
    );

    try {
      const r = await api.post(
        `/projects/${projectId}/crawl-tenders`
      );

      toast.success(
        `Crawl complete — ${r.data?.saved || 0} tenders captured.`
      );

      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Crawl failed"
      );
    } finally {
      setCrawling(false);
      setProjects((prev) =>
        prev.map((project) =>
          project.id === projectId
            ? { ...project, is_running: false }
            : project
        )
      );
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8"
      data-testid="tenders-page"
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1F3D] flex items-center gap-2">
            <Gavel className="h-6 w-6 text-[#0F1F3D]" />
            Tenders
          </h1>

          <p className="text-sm text-[#64748B] mt-1">
            Discover and track relevant tenders across projects and sources.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 lg:w-80">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="tenders-search"
              placeholder="Search here by Tender ID"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#CBD5E1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20"
            />
          </div>

          <button
            onClick={() => setSavedOnly((v) => !v)}
            data-testid="tenders-saved-toggle"
            aria-label="Show saved tenders"
            title={savedOnly ? "Showing saved" : "Show saved"}
            className={`h-11 w-11 shrink-0 flex items-center justify-center rounded-lg border transition-colors ${
              savedOnly
                ? "bg-[#0F1F3D] text-white border-[#0F1F3D]"
                : "bg-white text-[#475569] border-[#CBD5E1] hover:border-[#0F1F3D]"
            }`}
          >
            {savedOnly ? (
              <BookmarkCheck className="h-4 w-4" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {projectsLoaded && projects.length === 0 && (
        <div
          className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center"
          data-testid="tenders-no-projects"
        >
          <p className="text-[#64748B]">No projects yet.</p>
          {!isOrgMember && (
            <Link
              to="/app/projects/new"
              data-testid="tenders-create-project"
              className="text-[#0F1F3D] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 rounded-sm"
            >
              Click Here to create your first project
            </Link>
          )}
        </div>
      )}

      {!(projectsLoaded && projects.length === 0) && (
        <>
      {/* Saved views */}
      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="tenders-saved-views"
      >
        <span className="text-xs font-semibold text-[#64748B] inline-flex items-center gap-1">
          <Star className="h-3.5 w-3.5 text-[#F59E0B]" />
          Saved views:
        </span>

        {savedViews.length === 0 ? (
          <span className="text-xs text-[#94A3B8]">
            none yet
          </span>
        ) : (
          savedViews.map((v) => (
            <span
              key={v.id}
              className="group inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full bg-white border border-[#E2E8F0] text-xs font-medium text-[#334155] hover:border-[#0F1F3D]"
            >
              <button
                onClick={() => applyView(v)}
                data-testid={`tenders-view-apply-${v.id}`}
                className="hover:text-[#0F1F3D]"
              >
                {v.name}
              </button>

              <button
                onClick={() => deleteView(v.id)}
                data-testid={`tenders-view-delete-${v.id}`}
                aria-label="Delete view"
                className="text-[#CBD5E1] hover:text-[#EF4444]"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))
        )}

        <button
          onClick={saveCurrentView}
          data-testid="tenders-save-view"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-dashed border-[#CBD5E1] text-xs font-semibold text-[#0F1F3D] hover:border-[#0F1F3D] hover:bg-[#F8FAFC]"
        >
          <Plus className="h-3 w-3" />
          Save current view
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded-xl border border-[#E2E8F0] bg-white p-4 mb-5"
        data-testid="tenders-filters"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[170px] flex-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Project
            </label>

            <div className="mt-1">
              <StyledSelect
                options={projectOptions}
                value={projectId}
                onChange={setProjectId}
                testid="tenders-filter-project"
              />
            </div>
          </div>

          <div className="min-w-[150px] flex-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Status
            </label>

            <div className="mt-1">
              <StyledSelect
                options={STATUS_OPTIONS}
                value={status}
                onChange={setStatus}
                testid="tenders-filter-status"
              />
            </div>
          </div>

          <div className="min-w-[150px] flex-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Recommendation
            </label>

            <div className="mt-1">
              <StyledSelect
                options={REC_OPTIONS}
                value={recStatus}
                onChange={setRecStatus}
                testid="tenders-filter-recommendation"
              />
            </div>
          </div>

          <div className="min-w-[150px] flex-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Value
            </label>

            <div className="mt-1">
              <StyledSelect
                options={VALUE_OPTIONS}
                value={valueBand}
                onChange={setValueBand}
                testid="tenders-filter-value"
              />
            </div>
          </div>

          <div className="min-w-[150px] flex-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Deadline
            </label>

            <div className="mt-1">
              <StyledSelect
                options={DEADLINE_OPTIONS}
                value={deadline}
                onChange={setDeadline}
                testid="tenders-filter-deadline"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pb-0.5">
            {!isOrgMember && projectId !== "all" && (
              <button
                onClick={crawlNow}
                disabled={crawling || projectRunning}
                data-testid="tenders-crawl-now"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0F1F3D] text-white text-sm font-semibold hover:bg-[#172A4F] disabled:opacity-60 whitespace-nowrap"
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    crawling || projectRunning ? "animate-spin" : ""
                  }`}
                />

                {crawling || projectRunning ? "Crawling…" : "Crawl now"}
              </button>
            )}

            {activeFilters > 0 && (
              <button
                onClick={clearFilters}
                data-testid="tenders-clear-filters"
                className="inline-flex h-10 items-center text-sm font-semibold text-[#059669] hover:underline whitespace-nowrap"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Result Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div
          className="text-sm text-[#64748B]"
          data-testid="tenders-count"
        >
          <span className="font-bold text-[#0F1F3D]">
            {filtered.length}
          </span>{" "}
          tender{filtered.length === 1 ? "" : "s"} found
        </div>

        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button
              onClick={() => setSelectMode(true)}
              data-testid="tenders-select-mode-on"
              disabled={filtered.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] text-sm font-medium transition-colors disabled:opacity-50"
            >
              <SquareCheck className="h-4 w-4" />
              Select
            </button>
          ) : (
            <>
              <button
                onClick={toggleSelectAll}
                data-testid="tenders-select-all"
                className="text-sm text-[#0F1F3D] hover:underline font-medium whitespace-nowrap"
              >
                {isAllSelected ? "Deselect all" : `Select all (${filtered.length})`}
              </button>
              <button
                onClick={exitSelectMode}
                data-testid="tenders-select-mode-off"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0F1F3D] text-sm font-medium"
              >
                Cancel
              </button>
            </>
          )}
          <span className="text-sm text-[#64748B] hidden sm:inline">
            Sort by
          </span>

          <div className="w-52">
            <StyledSelect
              options={SORT_OPTIONS}
              value={sort}
              onChange={setSort}
              testid="tenders-sort"
            />
          </div>
        </div>
      </div>

      {selectMode && selected.size > 0 && (
        <div
          className="mb-4 sticky top-0 z-20 flex items-center gap-3 px-5 py-3 bg-[#0F1F3D] text-white rounded-lg shadow-lg"
          data-testid="tenders-bulk-bar"
        >
          <Check className="h-4 w-4" />
          <span className="text-sm font-medium" data-testid="tenders-bulk-count">
            {selected.size} selected
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/10 font-medium"
            >
              Clear
            </button>
            <button
              onClick={askBulkHide}
              data-testid="tenders-bulk-delete"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#DC2626] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete {selected.size}
            </button>
          </div>
        </div>
      )}

      <div
        className="rounded-xl border border-[#B45309]/25 bg-[#FFFBEB] px-4 py-2 mb-4 flex items-center gap-2"
        data-testid="tenders-scope-note"
      >
        <Info className="h-3.5 w-3.5 text-[#B45309] shrink-0" />

        <span className="text-xs text-[#92400E]">
          Currently covering{" "}
          <strong>Indian government tenders only</strong>.
        </span>
      </div>

      {loading ? (
        <div
          className="p-10 text-[#64748B]"
          data-testid="tenders-loading"
        >
          Loading tenders…
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-12 text-center"
          data-testid="tenders-empty"
        >
          <Gavel className="h-8 w-8 text-[#CBD5E1] mx-auto mb-3" />

          <p className="text-[#64748B] max-w-md mx-auto">
            {activeFilters > 0
              ? "No tenders match your filters. Try clearing them."
              : "No tenders captured yet. Enable the Tenders use-case on a project, add 1–3 portal URLs, then run the pipeline or hit Crawl now."}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="tenders-table"
              >
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-[#64748B]">
                    {selectMode && (
                      <th className="pl-4 pr-0 py-3 w-10" aria-hidden="true" />
                    )}
                    <th className="text-left font-semibold px-5 py-3">
                      Tender Details
                    </th>

                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      Tender ID
                    </th>

                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      Value (INR)
                    </th>

                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      E-Publish Date
                    </th>

                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      Deadline
                    </th>

                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                      Opening Date
                    </th>

                    <th className="text-right font-semibold px-5 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pageItems.map((t) => {
                    const isSelected = selected.has(t.id);
                    return (
                    <tr
                      key={t.id}
                      onClick={() => {
                        if (selectMode) toggleSelect(t);
                      }}
                      className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] transition-colors ${selectMode && isSelected ? "bg-[#F8FAFC]" : ""} ${selectMode ? "cursor-pointer" : ""}`}
                      data-testid={`tender-row-${t.id}`}
                      data-selected={isSelected ? "true" : "false"}
                    >
                      {selectMode && (
                        <td className="pl-4 pr-0 py-4 w-10 align-top" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => toggleSelect(t)}
                            aria-label={isSelected ? "Deselect" : "Select"}
                            data-testid={`tender-select-${t.id}`}
                            className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${isSelected
                              ? "bg-[#0F1F3D] border-[#0F1F3D] text-white"
                              : "bg-white border-[#CBD5E1] hover:border-[#0F1F3D]"
                              }`}
                          >
                            {isSelected && <Check className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                      )}
                      {/* Tender Details */}
                      <td className="px-5 py-4 max-w-sm">
                        <button
                          type="button"
                          onClick={(e) => {
                            if (selectMode) {
                              e.stopPropagation();
                              toggleSelect(t);
                            } else {
                              openTender(t);
                            }
                          }}
                          data-testid={`tender-open-detail-${t.id}`}
                          className="text-left font-semibold text-[#0F1F3D] leading-snug hover:underline"
                        >
                          {t.title}
                        </button>

                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          {projectMap[t.project_id] && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold"
                              data-testid={`tender-project-${t.id}`}
                            >
                              {projectMap[t.project_id]}
                            </span>
                          )}
                          {typeof t.relevance_score === "number" && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${relevanceClass(t.relevance_score)}`}
                              data-testid={`tender-relevance-${t.id}`}
                            >
                              {t.relevance_score}
                            </span>
                          )}
                          {t.action_required && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide ${(t.action_status || "open") === "closed" ? "bg-[#F1F5F9] text-[#475569]" : "bg-[#ECFDF5] text-[#047857]"}`}
                              data-testid={`tender-rec-status-${t.id}`}
                              title={t.action_closed_note ? `Closing note: ${t.action_closed_note}` : undefined}
                            >
                              {(t.action_status || "open") === "closed" ? "Closed" : "Open"}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Tender ID + Source */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div
                          className="text-[#475569] font-medium"
                          data-testid={`tender-id-${t.id}`}
                        >
                          {t.tender_id || "—"}
                        </div>

                        <div className="text-xs text-[#94A3B8] mt-1">
                          {t.source || "—"}
                        </div>
                      </td>

                      {/* Value */}
                      <td className="px-4 py-4 text-[#0F1F3D] font-medium whitespace-nowrap">
                        {t.value || "—"}
                      </td>
                      
                      {/* E-Publish Date */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <EPublishDateCell t={t} />
                      </td>

                      {/* Deadline */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <DeadlineCell t={t} />
                      </td>

                      {/* Opening Date */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <OpeningDateCell t={t} />
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(t);
                            }}
                            data-testid={`tender-bookmark-${t.id}`}
                            aria-label={
                              bookmarkIds.has(t.id)
                                ? "Remove bookmark"
                                : "Save tender"
                            }
                            className={`h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${
                              bookmarkIds.has(t.id)
                                ? "bg-[#0F1F3D] text-white border-[#0F1F3D]"
                                : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0F1F3D] hover:text-[#0F1F3D]"
                            }`}
                          >
                            {bookmarkIds.has(t.id) ? (
                              <BookmarkCheck className="h-4 w-4" />
                            ) : (
                              <Bookmark className="h-4 w-4" />
                            )}
                          </button>
                          {  /*
                          {t.url && (
                            <a
                              href={t.url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`tender-open-${t.id}`}
                              aria-label="Open tender"
                              className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#64748B] hover:border-[#0F1F3D] hover:text-[#0F1F3D] transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                            */}
                            {t.url && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTenderExternal(t);
                                }}
                                data-testid={`tender-open-${t.id}`}
                                aria-label="Open tender"
                                className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#64748B] hover:border-[#0F1F3D] hover:text-[#0F1F3D] transition-colors"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </button>
                            )}

                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteTender(t);
                            }}
                            data-testid={`tender-delete-${t.id}`}
                            aria-label="Hide tender"
                            title="Hide this tender"
                            className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#64748B] hover:border-[#EF4444] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 mt-4">
            <div
              className="text-xs text-[#94A3B8]"
              data-testid="tenders-range"
            >
              Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
              {Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
              {filtered.length} tenders
            </div>

            {totalPages > 1 && (
              <div
                className="flex items-center gap-1.5"
                data-testid="tenders-pagination"
              >
                <button
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  disabled={page === 1}
                  data-testid="tenders-prev"
                  className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                )
                  .filter(
                    (n) =>
                      n === 1 ||
                      n === totalPages ||
                      Math.abs(n - page) <= 1
                  )
                  .reduce((acc, n) => {
                    if (
                      acc.length &&
                      n - acc[acc.length - 1] > 1
                    ) {
                      acc.push("…");
                    }

                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span
                        key={`e${i}`}
                        className="px-2 text-[#94A3B8]"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => setPage(n)}
                        data-testid={`tenders-page-${n}`}
                        className={`h-9 min-w-9 px-3 flex items-center justify-center rounded-lg border text-sm font-medium ${
                          n === page
                            ? "bg-[#ECFDF5] border-[#059669] text-[#047857]"
                            : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#0F1F3D]"
                        }`}
                      >
                        {n}
                      </button>
                    )
                  )}

                <button
                  onClick={() =>
                    setPage((p) =>
                      Math.min(totalPages, p + 1)
                    )
                  }
                  disabled={page === totalPages}
                  data-testid="tenders-next"
                  className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
        </>
      )}

      <TenderModal
        tender={activeTender}
        projectName={
          activeTender
            ? projectMap[activeTender.project_id]
            : ""
        }
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setActiveTender(null);
        }}
        isBookmarked={
          activeTender
            ? bookmarkIds.has(activeTender.id)
            : false
        }
        onToggleBookmark={toggleBookmark}
        onActionStatusChange={onTenderActionStatusChange}
      />

      <AlertDialog open={!!tenderLinkModal} onOpenChange={(open) => !open && setTenderLinkModal(null)}>
        <AlertDialogContent data-testid="tender-link-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Open this tender on the portal</AlertDialogTitle>
            <AlertDialogDescription>
              Direct links to this portal often expire quickly. Copy the{" "}
              {tenderLinkModal?.tender_id ? "Tender ID" : "tender title"} below,
              then search for it on the portal's own search page.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {tenderLinkModal && (
            <div className="space-y-3 py-2">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
                  {tenderLinkModal.tender_id ? "Tender ID" : "Tender Title"}
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm font-mono text-[#0F1F3D] break-all">
                    {tenderLinkModal.tender_id || tenderLinkModal.title || "Not available"}
                  </code>
                  {(tenderLinkModal.tender_id || tenderLinkModal.title) && (
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(tenderLinkModal.tender_id || tenderLinkModal.title)
                      }
                      data-testid="tender-link-copy"
                      className="shrink-0 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-semibold text-[#0F1F3D] hover:border-[#0F1F3D] transition-colors"
                    >
                      {copied ? "Copied ✓" : "Copy"}
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-lg bg-[#FFFBEB] border border-[#B45309]/25 px-3 py-2 text-xs text-[#92400E]">
                Click "Open portal" below, wait for it to load, then paste{" "}
                {tenderLinkModal.tender_id ? "the Tender ID" : "the title"} into its
                search box (usually labeled "Search" or "Tender Search").
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => visitPortal(tenderLinkModal)}
              className="bg-[#0F1F3D] hover:bg-[#172A4F] text-white"
              data-testid="tender-link-visit"
            >
              Open portal →
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDeleteView} onOpenChange={(open) => !open && setPendingDeleteView(null)}>
        <AlertDialogContent data-testid="tenders-view-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete saved view?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteView
                ? `This will remove “${pendingDeleteView.name}” from your saved views.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteView}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
              data-testid="tenders-view-delete-confirm"
            >
              Delete view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="tenders-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === "bulk"
                ? `Delete ${pendingDelete.items.length} tender${pendingDelete.items.length === 1 ? "" : "s"} from your feed?`
                : "Delete this item from your feed?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === "one" && pendingDelete.item && (
                <span className="block mb-3 italic text-[#0F1F3D]">
                  &ldquo;{pendingDelete.item.title || pendingDelete.item.tender_id || "This tender"}&rdquo;
                </span>
              )}
              The item{pendingDelete?.kind === "bulk" ? "s" : ""} will be hidden from your feed only — {pendingDelete?.kind === "bulk" ? "they remain" : "it remains"} in records and won&apos;t be re-fetched by the pipeline.
              You can undo this from the toast for a few seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenders-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
              data-testid="tenders-delete-confirm"
            >
              Delete{pendingDelete?.kind === "bulk" ? ` ${pendingDelete.items.length}` : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}